import { useCallback, useEffect, useState, useRef } from 'react';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useBulkAddSpansToListMutation } from '@/features/customListSlice';
import { LocalFilter } from '@/views/filter/LocalFilter';
import {
  type FilterState,
  initialState as initialFilterState,
} from '@/features/filterSlice';
import { generateBase64FilterQuery } from '@/features/filterHelpers';
import { useRunListAction } from '@/views/list/custom-lists/customListSelectorUtils';
import { incrementMvtCacheCounter } from '@/features/analysisMapSlice';
import { useAppDispatch } from '@/app/hooks';

interface AddSpansToProjectFilterProps {
  /** The project ID to add spans to */
  projectId: string;
  /** Whether the project is currently loading */
  isLoading: boolean;
  /** Total number of spans in the project (used to auto-open filter when empty) */
  totalSpans: number;
  /** Callback to refresh the project data after adding spans */
  onSpansAdded: () => void;
  /** Whether the filter is open (controlled) */
  isOpen?: boolean;
  /** Callback when the modal should open */
  requestOpen?: () => void;
  /** Callback when the modal should close */
  requestClose?: () => void;
}

export const AddSpansToProjectFilter = ({
  projectId,
  isLoading,
  totalSpans,
  onSpansAdded,
  isOpen = false,
  requestOpen = () => {},
  requestClose = () => {},
}: AddSpansToProjectFilterProps) => {
  const intl = useClientTerminology();
  const dispatch = useAppDispatch();
  const [bulkAddSpansToList] = useBulkAddSpansToListMutation();
  const runListAction = useRunListAction(requestClose);

  /**
   * This filter state is specifically for the "Add spans to project" flow
   */
  const [addSpansFilterState, setAddSpansFilterState] =
    useState<FilterState>(initialFilterState);

  // Track whether we've already auto-opened for empty spans to prevent reopening
  const hasAutoOpenedRef = useRef(false);

  // Auto-open the filter when the project has no spans (only once)
  useEffect(() => {
    if (
      !isLoading &&
      totalSpans === 0 &&
      !isOpen &&
      !hasAutoOpenedRef.current
    ) {
      hasAutoOpenedRef.current = true;
      requestOpen();
    }
  }, [totalSpans, isLoading, isOpen, requestOpen]);

  const handleAddSpansToProject = useCallback(
    async (newFilterState: FilterState) => {
      setAddSpansFilterState(newFilterState);

      await runListAction(async () => {
        const result = await bulkAddSpansToList({
          listId: projectId,
          body: {
            addition_filter: generateBase64FilterQuery(newFilterState),
          },
        }).unwrap();

        onSpansAdded();

        return result;
      });
    },
    [
      bulkAddSpansToList,
      projectId,
      runListAction,
      onSpansAdded,
      setAddSpansFilterState,
    ]
  );

  return (
    <LocalFilter
      isOpen={isOpen}
      onClose={() => {
        requestClose();
        dispatch(incrementMvtCacheCounter());
      }}
      initialState={addSpansFilterState}
      onApply={handleAddSpansToProject}
      config={{
        title: intl.formatMessage(
          { id: 'customList.steps' },
          {
            onStep: 2,
            ofStep: 2,
            action: intl.formatMessage({
              id: 'customList.stepActionFilterToAdd',
            }),
          }
        ),
        primaryLabel: intl.formatMessage({ id: 'customList.addToProject' }),
        primaryTestId: 'add-to-project-button',
      }}
    />
  );
};
